import { useContext, useEffect } from 'react';
import dayjs from 'dayjs';

import AuthContext from '../../context/authContext';
import { useAttention } from '../../context/attentionContext';
import { usePizzaBranch } from '../../context/pizzaBranchContext';
import useInternalBooking from '../../hooks/useInternalBooking';
import { ATTENTION_WINDOW_MS } from '../../util/attentionStripes';
import { useNoPizzaWarnings } from '../../util/pizzaWarnings';

const POLL_INTERVAL_MS = 30 * 1000;
const PIZZA_KEY_PREFIX = 'pizza:';

// Globally mounted watcher: polls the pizza overview regardless of the active
// view and raises a reusable attention pop-up as soon as a booking's planned
// serve time moves inside the attention window (the next 15 minutes) and stays
// undelivered. Mounted once in RootLayout so the pop-up can appear anywhere.
//
// Notifications are scoped to the location selected in the Pizza view
// (`notificationsBranchId`); when no concrete location is selected ("Alle
// Standorte") no reminders are raised at all.
const PizzaAttentionWatcher = () => {
  const authCtx = useContext(AuthContext);
  const { getPizzaOverview } = useInternalBooking();
  const { raiseAttention, releaseAttention } = useAttention();
  const { notificationsBranchId } = usePizzaBranch();
  // Device-local opt-out (localStorage only): suppresses pizza pop-ups on this
  // device regardless of the per-user preference.
  const [noPizzaWarnings] = useNoPizzaWarnings();

  const token = authCtx?.token;
  const canRead = authCtx?.checkRightAny?.('internalBooking:read:own');
  // Per-user opt-out: when disabled the user receives no pizza pop-ups at all.
  const popupsEnabled = authCtx?.preferences?.pizzaPopupNotifications !== false;

  useEffect(() => {
    // No reminders without a concrete location selection, when the user has
    // disabled pizza pop-ups in their settings, or when this device opted out.
    if (!token || !canRead || !notificationsBranchId || !popupsEnabled || noPizzaWarnings) {
      // Drop any pizza alerts that may still be queued from a previous scope.
      releaseAttention(key => key.startsWith(PIZZA_KEY_PREFIX));
      return undefined;
    }

    let cancelled = false;

    const check = async () => {
      let response;
      try {
        response = await getPizzaOverview({ branchId: notificationsBranchId });
      } catch (_) {
        // Background watcher must stay silent; the Pizza view surfaces errors.
        return;
      }
      if (cancelled) return;

      const bookings = Array.isArray(response?.bookings) ? response.bookings : [];
      const now = Date.now();
      const activeKeys = new Set();

      bookings.forEach(booking => {
        if (booking?.pizzaFulfillment?.deliveredAt) return;
        const serveAt = new Date(booking.plannedServeAt || booking.startTime).getTime();
        if (!serveAt) return;
        const remaining = serveAt - now;
        if (remaining > ATTENTION_WINDOW_MS) return;

        const key = `${PIZZA_KEY_PREFIX}${booking._id}:${serveAt}`;
        activeKeys.add(key);

        const overdue = remaining <= 0;
        raiseAttention({
          key,
          title: overdue ? 'Pizza fällig' : 'Pizza gleich fällig',
          message: `${booking.customerName} (#${booking.bookingNumber}) – Servierzeit ${dayjs(
            serveAt
          ).format('HH:mm')}`,
          detail: overdue
            ? 'Die geplante Servierzeit ist erreicht.'
            : `Servierzeit in ${Math.max(1, Math.round(remaining / 60000))} Minuten.`,
          severity: overdue ? 'error' : 'warning',
          primaryAction: { label: 'Zur Küche', to: '/kitchen' },
        });
      });

      // Allow re-triggering for pizzas that left the window (e.g. delivered then
      // reverted, or serve time pushed back out of range).
      releaseAttention(key => key.startsWith(PIZZA_KEY_PREFIX) && !activeKeys.has(key));
    };

    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // getPizzaOverview/raiseAttention/releaseAttention are stable enough; we key
    // the watcher lifecycle on auth state, the selected location and the opt-outs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, canRead, notificationsBranchId, popupsEnabled, noPizzaWarnings]);

  return null;
};

export default PizzaAttentionWatcher;
